import React from 'react'
import { useNavigate, Link } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { useAuthStore } from '@/stores/authStore'
import { usePatientStore } from '@/stores/patientStore'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useMessageStore } from '@/stores/messageStore'
import { useThemeStore } from '@/stores/themeStore'

const logoLight = '/beeldmerk-soft-beige.svg'
const logoDark = '/beeldmerk-moss-green.svg'

import { useTranslation } from 'react-i18next'

interface NavBarProps {
  title?: string | null
  showBack?: boolean
  showClose?: boolean
  onBack?: () => void
  onClose?: () => void
}

const NavBar = ({ title = null, showBack = false, showClose = false, onBack, onClose }: NavBarProps): React.ReactElement | null => {
  const { isAuthenticated, isLoading, isOnboarded, logout } = useAuthStore(
    useShallow((s) => ({ isAuthenticated: s.isAuthenticated, isLoading: s.isLoading, isOnboarded: s.isOnboarded, logout: s.logout }))
  )
  const loadFailed = usePatientStore((s) => s.loadFailed)
  const navigate = useNavigate()
  const clear = useMessageStore((s) => s.clear)
  const { t } = useTranslation('navigation')
  const effective = useThemeStore((s) => s.effective)
  const logo = effective === 'dark' ? logoLight : logoDark

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!isAuthenticated) return null

  const handleBack = (): void => {
    if (onBack) onBack()
    else navigate('/dashboard')
  }

  const handleClose = (): void => {
    clear()
    if (onClose) onClose()
    else navigate('/')
  }

  const isPageVariant = !!title

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] bg-beige flex flex-col items-center transition-colors duration-300">
      <div className="w-full max-w-[860px] px-4 flex items-center justify-between h-[60px]">
        {isPageVariant ? (
          <>
            {showBack ? (
              <button
                className="text-[1.5rem] text-text bg-transparent border-none cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2"
                onClick={handleBack}
                data-testid="prev_button"
                aria-label="Terug"
              >
                ←
              </button>
            ) : (
              <span className="w-[30px]" />
            )}

            <h1 className="flex-1 text-center !text-[1.25rem] m-0 text-text font-stara">
              {title}
            </h1>

            {showClose ? (
              <button
                className="text-[1.8rem] text-text bg-transparent border-none cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center leading-none focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2"
                onClick={handleClose}
                aria-label="Sluiten"
              >
                ×
              </button>
            ) : (
              <span className="w-[30px]" />
            )}
          </>
        ) : (
          <>
            <div className="flex-1" />
            <Link to="/dashboard" aria-label={t('navigation.dashboard')}>
              <img src={logo} alt="logo" className="logo" />
            </Link>
            <div className="flex-1 flex justify-end">
              {/* Niet-onboarded gebruikers kunnen /settings niet bereiken, dus uitloggen moet hier */}
              {(isOnboarded === false || (isOnboarded === null && loadFailed)) && (
                <button
                  className="text-[14px] leading-[24px] text-text bg-transparent border-none cursor-pointer min-h-[44px] px-2 flex items-center focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2"
                  data-testid="navbar-logout-button"
                  onClick={logout}
                >
                  {t('navigation.logoff')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
  </header>
  )
}

export default NavBar
